import React, { useState, useEffect } from "react";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useApp } from "../App";

const IconPhone = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <path d="M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1C10.6 21 3 13.4 3 4c0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.3 0 .7-.2 1L6.6 10.8z" stroke="#1a2744" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const IconGlobe = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="9" stroke="#1a2744" strokeWidth="1.5"/>
    <path d="M12 3c-2.5 3-4 5.5-4 9s1.5 6 4 9M12 3c2.5 3 4 5.5 4 9s-1.5 6-4 9M3 12h18" stroke="#1a2744" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

const IconInfo = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="9" stroke="#1a2744" strokeWidth="1.5"/>
    <path d="M12 11v6" stroke="#1a2744" strokeWidth="1.5" strokeLinecap="round"/>
    <circle cx="12" cy="7.5" r="1" fill="#1a2744"/>
  </svg>
);

const IconShield = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <path d="M12 3L4 7v5c0 4.4 3.4 8.5 8 9.5 4.6-1 8-5.1 8-9.5V7l-8-4z" stroke="#1a2744" strokeWidth="1.5" strokeLinejoin="round"/>
    <path d="M9 12l2 2 4-4" stroke="#1a2744" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const IconMail = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <rect x="3" y="5" width="18" height="14" rx="2" stroke="#1a2744" strokeWidth="1.5"/>
    <path d="M3 7l9 7 9-7" stroke="#1a2744" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

const IconSignOut = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" stroke="#c0392b" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const rowIconStyle = {
  width: 32, height: 32, borderRadius: 8,
  background: "#eef0f7",
  display: "flex", alignItems: "center", justifyContent: "center",
  flexShrink: 0,
};

function Screen({ title, onBack, children }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "var(--offwhite)", zIndex: 250, overflowY: "auto", maxWidth: 430, margin: "0 auto" }}>
      <div style={{ background: "var(--navy)", padding: "14px 16px", display: "flex", alignItems: "center", gap: 12 }}>
        <button onClick={onBack} style={{ background: "none", border: "none", color: "#fff", fontSize: 22, cursor: "pointer", lineHeight: 1, padding: 0 }}>←</button>
        <span style={{ color: "#fff", fontSize: 16, fontWeight: 600, fontFamily: "inherit" }}>{title}</span>
      </div>
      <div style={{ padding: "20px 16px 80px" }}>{children}</div>
    </div>
  );
}

function HowItWorks({ onBack, lang }) {
  const isEn = lang !== "pt";
  const sections = isEn ? [
    { title: "Buying", text: "Browse listings and filter by category or size. Found something? Tap the listing and contact the seller directly on WhatsApp. Agree on price, collection and payment between yourselves." },
    { title: "Selling", text: "Tap the + button to post a listing. Add a photo, pick the item from the list, choose your size, condition and price. Make sure your WhatsApp number is saved in your profile so buyers can reach you." },
    { title: "Payment", text: "MBWay or cash on collection — agreed directly between buyer and seller." },
    { title: "Collection", text: "Arrange directly with the seller. Most pickups happen at the school gate." },
    { title: "Moderation", text: "See something that shouldn't be here? Flag the listing and our moderator will review it." },
  ] : [
    { title: "Comprar", text: "Explore os anúncios e filtre por categoria ou tamanho. Encontrou algo? Toque no anúncio e contacte o vendedor directamente pelo WhatsApp. Combine preço, entrega e pagamento entre vocês." },
    { title: "Vender", text: "Toque no botão + para publicar um anúncio. Adicione uma foto, escolha o artigo da lista, seleccione o tamanho, estado e preço. Certifique-se que o seu número de WhatsApp está guardado no perfil para que os compradores possam contactá-lo." },
    { title: "Pagamento", text: "MBWay ou numerário na entrega — combinado directamente entre comprador e vendedor." },
    { title: "Entrega", text: "A combinar directamente com o vendedor. A maioria das entregas acontece no portão da escola." },
    { title: "Moderação", text: "Vê algo que não deveria estar aqui? Reporte o anúncio e o moderador irá analisá-lo." },
  ];

  return (
    <Screen title={isEn ? "How it works" : "Como funciona"} onBack={onBack}>
      {sections.map((s, i) => (
        <div key={i} style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: "var(--navy)", marginBottom: 6 }}>{s.title}</div>
          <div style={{ fontSize: 14, color: "#555", lineHeight: 1.7 }}>{s.text}</div>
          {i < sections.length - 1 && <div style={{ height: 1, background: "var(--border)", marginTop: 20 }} />}
        </div>
      ))}
      <div style={{ marginTop: 8, padding: "12px 16px", background: "var(--navy-pale)", borderRadius: 10, fontSize: 12, color: "var(--navy)", lineHeight: 1.6 }}>
        kepti is a community project. Pass it on. 🤝
      </div>
    </Screen>
  );
}

function Privacy({ onBack, lang }) {
  const isEn = lang !== "pt";
  return (
    <Screen title={isEn ? "Privacy & Disclaimer" : "Privacidade e Aviso Legal"} onBack={onBack}>
      <div style={{ fontSize: 13, color: "#555", lineHeight: 1.8, display: "flex", flexDirection: "column", gap: 20 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "var(--navy)", marginBottom: 6 }}>{isEn ? "About kepti" : "Sobre o kepti"}</div>
          <p>{isEn ? "kepti is a free, community-run marketplace created by a parent to help families buy and sell second-hand school uniforms. It is not affiliated with, endorsed by, or operated by any school." : "O kepti é um mercado comunitário gratuito criado por um pai para ajudar as famílias a comprar e vender fardas escolares em segunda mão. Não é afiliado, aprovado ou operado por nenhuma escola."}</p>
        </div>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "var(--navy)", marginBottom: 6 }}>{isEn ? "No liability" : "Isenção de responsabilidade"}</div>
          <p>{isEn ? "kepti is provided as-is, as a community service. All transactions, exchanges, payments and collections are carried out solely between buyers and sellers. The creator of kepti accepts no responsibility whatsoever for any disputes, losses, damages, failed transactions, non-delivery of items, fraud, or any other issues arising from the use of this platform. All risks are borne exclusively by the users involved in each transaction. While this disclaimer may not limit liability in all legal jurisdictions, it represents the full intent of the platform creator." : "O kepti é disponibilizado tal como está, como serviço comunitário. Todas as transacções, trocas, pagamentos e entregas são realizados exclusivamente entre compradores e vendedores. O criador do kepti não aceita qualquer responsabilidade por disputas, perdas, danos, transacções falhadas, não entrega de artigos, fraude ou quaisquer outros problemas decorrentes da utilização desta plataforma. Todos os riscos são da exclusiva responsabilidade dos utilizadores envolvidos em cada transacção."}</p>
        </div>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "var(--navy)", marginBottom: 6 }}>{isEn ? "Your data (GDPR)" : "Os seus dados (RGPD)"}</div>
          <p>{isEn ? "kepti stores only the information you provide: your name, email address (via Google login), WhatsApp number, and listings you post. This data is used solely to operate the marketplace. It is never sold or shared with third parties. You may request deletion of your data at any time by contacting hello@kepti.app. kepti complies with the General Data Protection Regulation (GDPR) as applicable under Portuguese and EU law." : "O kepti armazena apenas as informações que fornece: nome, email (via Google), número de WhatsApp e anúncios publicados. Estes dados são utilizados exclusivamente para operar o mercado. Nunca são vendidos nem partilhados. Pode solicitar a eliminação dos seus dados através de hello@kepti.app."}</p>
        </div>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "var(--navy)", marginBottom: 6 }}>{isEn ? "Contact" : "Contacto"}</div>
          <p>hello@kepti.app</p>
        </div>
        <div style={{ fontSize: 11, color: "#aaa", borderTop: "0.5px solid var(--border)", paddingTop: 16 }}>
          {isEn ? "Last updated: May 2026" : "Última actualização: Maio 2026"}
        </div>
      </div>
    </Screen>
  );
}

function WhatsAppModal({ onClose, current, onSave }) {
  const [number, setNumber] = useState(current || "");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!number.trim()) return;
    setSaving(true);
    await onSave(number.trim());
    setSaving(false);
    onClose();
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 300, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
      <div style={{ background: "#fff", borderRadius: "20px 20px 0 0", width: "100%", maxWidth: 430, padding: "20px 20px 40px" }}>
        <div style={{ width: 36, height: 4, background: "var(--border)", borderRadius: 2, margin: "0 auto 16px" }} />
        <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 6 }}>WhatsApp number</div>
        <div style={{ fontSize: 13, color: "#888", marginBottom: 20, lineHeight: 1.5 }}>
          Buyers will use this to contact you. Include your country code — e.g. +351 912 345 678
        </div>
        <input className="form-input" type="tel" placeholder="+351 912 345 678" value={number} onChange={e => setNumber(e.target.value)} autoFocus />
        <button className="btn-primary" onClick={handleSave} disabled={saving || !number.trim()} style={{ marginTop: 8 }}>
          {saving ? "Saving..." : "Save number"}
        </button>
        <button onClick={onClose} style={{ width: "100%", padding: "12px", marginTop: 10, background: "none", border: "none", color: "var(--text-muted)", fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>
          Cancel
        </button>
      </div>
    </div>
  );
}

export default function Profile() {
  const { t, user, handleSignOut, toggleLang, lang } = useApp();
  const [screen, setScreen] = useState(null);
  const [showWA, setShowWA] = useState(false);
  const [waNumber, setWaNumber] = useState("");

  useEffect(() => {
    if (!user) return;
    getDoc(doc(db, "users", user.uid)).then(d => {
      if (d.exists()) setWaNumber(d.data().whatsapp || "");
    });
  }, [user]);

  const saveWhatsApp = async (number) => {
    await setDoc(doc(db, "users", user.uid), {
      whatsapp: number,
      name: user.displayName,
      email: user.email,
    }, { merge: true });
    setWaNumber(number);
  };

  const handleContactModerator = () => {
    window.location.href = "mailto:hello@kepti.app?subject=kepti%20—%20Message%20from%20a%20parent";
  };

  if (screen === "how") return <HowItWorks onBack={() => setScreen(null)} lang={lang} />;
  if (screen === "privacy") return <Privacy onBack={() => setScreen(null)} lang={lang} />;

  return (
    <div className="profile-page">
      <div className="profile-header">
        <div className="profile-avatar">
          {user?.photoURL
            ? <img src={user.photoURL} alt={user.displayName} referrerPolicy="no-referrer" />
            : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28, fontWeight: 700, color: "var(--navy)" }}>
                {user?.displayName?.[0] || "?"}
              </div>
          }
        </div>
        <div className="profile-name">{user?.displayName}</div>
        <div className="profile-email">{user?.email}</div>
      </div>

      <div className="profile-section">
        <div className="profile-section-title">Account</div>
        <div className="profile-row" onClick={() => setShowWA(true)}>
          <div style={rowIconStyle}><IconPhone /></div>
          <span className="profile-row-label">WhatsApp number</span>
          <span className="profile-row-value" style={{ color: waNumber ? "var(--navy)" : "#c0392b", fontWeight: waNumber ? 400 : 600 }}>
            {waNumber || "⚠ Required"} →
          </span>
        </div>
        <div className="profile-row" onClick={toggleLang}>
          <div style={rowIconStyle}><IconGlobe /></div>
          <span className="profile-row-label">Language</span>
          <span className="profile-row-value">{lang === "en" ? "English" : "Português"} →</span>
        </div>
      </div>

      <div className="profile-section" style={{ marginTop: 20 }}>
        <div className="profile-section-title">About</div>
        <div className="profile-row" onClick={() => setScreen("how")}>
          <div style={rowIconStyle}><IconInfo /></div>
          <span className="profile-row-label">How it works</span>
          <span className="profile-row-value">→</span>
        </div>
        <div className="profile-row" onClick={() => setScreen("privacy")}>
          <div style={rowIconStyle}><IconShield /></div>
          <span className="profile-row-label">Privacy & disclaimer</span>
          <span className="profile-row-value">→</span>
        </div>
        <div className="profile-row" onClick={handleContactModerator}>
          <div style={rowIconStyle}><IconMail /></div>
          <span className="profile-row-label">Contact moderator</span>
          <span className="profile-row-value">hello@kepti.app →</span>
        </div>
      </div>

      <div className="profile-section" style={{ marginTop: 20 }}>
        <div className="profile-row" onClick={handleSignOut} style={{ justifyContent: "center", gap: 8 }}>
          <IconSignOut />
          <span style={{ fontSize: 15, fontWeight: 600, color: "#c0392b", fontFamily: "inherit" }}>{t.signOut}</span>
        </div>
      </div>

      <div style={{ height: 40 }} />

      {showWA && (
        <WhatsAppModal
          onClose={() => setShowWA(false)}
          current={waNumber}
          onSave={saveWhatsApp}
        />
      )}
    </div>
  );
}
